import { combineReducers } from 'redux'

import {
  GET_ATTACK_GENERATION_DATA_ASYNC,
  GET_COMPANY_RISK_ASYNC,
  GET_COMPANY_RISK_TREND_ASYNC,
  GET_RED_TEAM_STATUS_ASYNC,
  GET_TRAINING_ACTIVITY_ASYNC,
  SET_TRAINING_ACTIVITY_PAGE,
  SUBMIT_ATTACK_FEEDBACK_ASYNC,
} from './constants'

const formatDateTime = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  const month = date.toLocaleString('en-US', { month: 'short' })
  const day = date.getDate()
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  return `${month} ${day}, ${h12}:${minutes} ${ampm}`
}

const formatStoryDate = (isoString) => {
  if (!isoString) return ''
  const date = new Date(isoString)
  const month = date.toLocaleString('en-US', { month: 'short' })
  const day = date.getDate()
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const h12 = hours % 12 || 12
  return `${month} ${day}, ${year} - ${h12}:${minutes} ${ampm}`
}

const initialState = {
  companyProfile: null,
  intelligenceSources: { linkedin: 0, facebook: 0, news: 0 },
  defenseCoverage: 0,
  isAgentActive: false,
  osintCreditsLeft: null,
  osintLastUpdate: null,
  osintNextSchedule: null,
  attackGenerationData: [],
  companyRisk: null,
  riskTrendWeeks: [],
  trainingActivity: [],
  trainingActivityTotal: 0,
  trainingActivityPage: 0,
  loading: {
    status: false,
    attackGenerationData: false,
    companyRisk: false,
    riskTrend: false,
    trainingActivity: false,
  },
  error: {
    status: null,
    attackGenerationData: null,
    companyRisk: null,
    riskTrend: null,
    trainingActivity: null,
  },
}

const redTeamReducer = (state = initialState, action) => {
  switch (action.type) {
    // ── Company Profile (was "Status") ────────────────────────────
    case GET_RED_TEAM_STATUS_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, status: true },
        error: { ...state.error, status: null },
      }
    case GET_RED_TEAM_STATUS_ASYNC.SUCCESS: {
      const profile = action.payload
      let osintNextSchedule = state.osintNextSchedule
      if (profile.nextOsintSchedule) {
        osintNextSchedule = formatDateTime(profile.nextOsintSchedule)
      } else if (profile.nextScheduledRunDate) {
        osintNextSchedule = formatDateTime(profile.nextScheduledRunDate)
      }

      return {
        ...state,
        companyProfile: profile,
        intelligenceSources: profile.sources || state.intelligenceSources,
        defenseCoverage: profile.defenseCoveragePercent ?? 0,
        osintCreditsLeft: profile.osintCreditsLeft ?? state.osintCreditsLeft,
        osintLastUpdate: profile.lastOsintScan
          ? formatDateTime(profile.lastOsintScan)
          : state.osintLastUpdate,
        osintNextSchedule,
        isAgentActive: profile.isActiveAgent ?? state.isAgentActive,
        loading: { ...state.loading, status: false },
        error: { ...state.error, status: null },
      }
    }
    case GET_RED_TEAM_STATUS_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, status: false },
        error: { ...state.error, status: action.error },
      }
    case GET_RED_TEAM_STATUS_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, status: false },
      }

    // ── Attack Progress (was "Attack Generation Data") ────────────
    case GET_ATTACK_GENERATION_DATA_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, attackGenerationData: true },
        error: { ...state.error, attackGenerationData: null },
      }
    case GET_ATTACK_GENERATION_DATA_ASYNC.SUCCESS: {
      const periods = action.payload?.periods || []
      const chartData = periods.map((p) => ({
        week: p.periodLabel,
        riskSurface: p.potentialAttacks,
        learnedAttacks: p.learnedAttacks,
      }))
      return {
        ...state,
        attackGenerationData: chartData,
        loading: { ...state.loading, attackGenerationData: false },
        error: { ...state.error, attackGenerationData: null },
      }
    }
    case GET_ATTACK_GENERATION_DATA_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, attackGenerationData: false },
        error: { ...state.error, attackGenerationData: action.error },
      }
    case GET_ATTACK_GENERATION_DATA_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, attackGenerationData: false },
      }

    // ── Company Risk ──────────────────────────────────────────────
    case GET_COMPANY_RISK_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, companyRisk: true },
        error: { ...state.error, companyRisk: null },
      }
    case GET_COMPANY_RISK_ASYNC.SUCCESS:
      return {
        ...state,
        companyRisk: action.payload,
        loading: { ...state.loading, companyRisk: false },
        error: { ...state.error, companyRisk: null },
      }
    case GET_COMPANY_RISK_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, companyRisk: false },
        error: { ...state.error, companyRisk: action.error },
      }
    case GET_COMPANY_RISK_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, companyRisk: false },
      }

    // ── Risk Trend ────────────────────────────────────────────────
    case GET_COMPANY_RISK_TREND_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, riskTrend: true },
        error: { ...state.error, riskTrend: null },
      }
    case GET_COMPANY_RISK_TREND_ASYNC.SUCCESS: {
      const weeks = action.payload?.weeks || []
      return {
        ...state,
        riskTrendWeeks: weeks,
        loading: { ...state.loading, riskTrend: false },
        error: { ...state.error, riskTrend: null },
      }
    }
    case GET_COMPANY_RISK_TREND_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, riskTrend: false },
        error: { ...state.error, riskTrend: action.error },
      }
    case GET_COMPANY_RISK_TREND_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, riskTrend: false },
      }

    // ── Stories (was "Training Activity") ──────────────────────────
    case GET_TRAINING_ACTIVITY_ASYNC.INITIATE:
      return {
        ...state,
        loading: { ...state.loading, trainingActivity: true },
        error: { ...state.error, trainingActivity: null },
      }
    case GET_TRAINING_ACTIVITY_ASYNC.SUCCESS: {
      const stories = action.payload?.stories || []
      const items = stories.map((story) => ({
        id: story.id,
        type: story.attackEmail ? 'adapted' : 'osint',
        title: story.title,
        timestamp: formatStoryDate(story.createdAt),
        description: story.summary,
        source: story.sources?.[0]?.sourceName || null,
        sourceUrl: story.sources?.[0]?.url || null,
        kind: story.sources?.[0]?.kind || null,
        sources: story.sources || [],
        showDetailsAction: !!story.attackEmail,
        body: story.attackEmail?.body || null,
        attackEmailId: story.attackEmail?.id || null,
        feedback: story.attackEmail?.feedback || null,
        confirmedFeedback: story.attackEmail?.feedback || null,
      }))
      return {
        ...state,
        trainingActivity: items,
        trainingActivityTotal: action.payload?.total || 0,
        loading: { ...state.loading, trainingActivity: false },
        error: { ...state.error, trainingActivity: null },
      }
    }
    case GET_TRAINING_ACTIVITY_ASYNC.ERROR:
      return {
        ...state,
        loading: { ...state.loading, trainingActivity: false },
        error: { ...state.error, trainingActivity: action.error },
      }
    case GET_TRAINING_ACTIVITY_ASYNC.CANCEL:
      return {
        ...state,
        loading: { ...state.loading, trainingActivity: false },
      }

    // ── Attack Feedback (optimistic) ────────────────────────────────
    case SUBMIT_ATTACK_FEEDBACK_ASYNC.INITIATE: {
      const { attackEmailId, feedback } = action.payload || {}
      return {
        ...state,
        trainingActivity: state.trainingActivity.map((item) =>
          item.attackEmailId === attackEmailId ? { ...item, feedback } : item
        ),
      }
    }
    case SUBMIT_ATTACK_FEEDBACK_ASYNC.SUCCESS: {
      const { attackEmailId, feedback } = action.payload || {}
      return {
        ...state,
        trainingActivity: state.trainingActivity.map((item) =>
          item.attackEmailId === attackEmailId
            ? { ...item, feedback, confirmedFeedback: feedback }
            : item
        ),
      }
    }
    case SUBMIT_ATTACK_FEEDBACK_ASYNC.ERROR: {
      const { attackEmailId } = action.payload || {}
      return {
        ...state,
        trainingActivity: state.trainingActivity.map((item) =>
          item.attackEmailId === attackEmailId
            ? { ...item, feedback: item.confirmedFeedback }
            : item
        ),
      }
    }

    case SET_TRAINING_ACTIVITY_PAGE:
      return {
        ...state,
        trainingActivityPage: action.payload,
      }

    default:
      return state
  }
}

export default function () {
  const initialCombined = combineReducers({
    self: redTeamReducer,
  })

  return (state = initialCombined, action) => state
}
